// Client for the agent realtime SSE stream (GET /api/realtime/agent-stream).
//
// The browser EventSource API can't send an Authorization header, so we first POST to
// /realtime/ticket (which DOES carry the bearer via the axios client) to mint a
// short-lived ticket, then open the EventSource with that ticket as a query param.
// Auto-reconnects with exponential backoff, minting a fresh ticket each time.

import { api } from '../api/client.js';

export interface WaitingChat {
  conversationId: string;
  contactId: string;
  sellerId: string;
  siteId: string | null;
  customer: string | null;
  sourceDomain: string | null;
  at: string;
}

export interface LiveChatMessage {
  conversationId: string;
  contactId: string;
  sender: 'customer' | 'agent' | 'system';
  displayName: string | null;
  body: string;
  at: string;
}

export interface AgentStreamHandlers {
  onWaiting(chat: WaitingChat): void;
  onCleared(contactId: string): void;
  onMessage?(msg: LiveChatMessage): void;
  onStatus?(connected: boolean): void;
}

const TICKET_PATH = '/realtime/ticket';
const STREAM_PATH = '/realtime/agent-stream';

function apiBase(): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
  return base.startsWith('http') ? base : `${window.location.origin}${base}`;
}

export function subscribeAgentStream(handlers: AgentStreamHandlers): () => void {
  let es: EventSource | null = null;
  let stopped = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let backoff = 1000;

  const scheduleReconnect = (): void => {
    if (stopped || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, backoff);
    backoff = Math.min(backoff * 2, 30_000);
  };

  const connect = async (): Promise<void> => {
    if (stopped) return;
    let ticket = '';
    try {
      const res = await api.post<{ success: boolean; data?: { ticket: string } }>(TICKET_PATH);
      ticket = res.data.data?.ticket ?? '';
      if (!ticket) throw new Error('no ticket');
    } catch {
      scheduleReconnect();
      return;
    }
    const url = `${apiBase().replace(/\/$/, '')}${STREAM_PATH}?ticket=${encodeURIComponent(ticket)}`;
    es = new EventSource(url);

    es.addEventListener('open', () => {
      backoff = 1000;
      handlers.onStatus?.(true);
    });
    es.addEventListener('chat.waiting', (ev) => {
      try {
        handlers.onWaiting(JSON.parse((ev as MessageEvent).data) as WaitingChat);
      } catch {
        /* ignore malformed frame */
      }
    });
    es.addEventListener('chat.cleared', (ev) => {
      try {
        const d = JSON.parse((ev as MessageEvent).data) as { contactId: string };
        handlers.onCleared(d.contactId);
      } catch {
        /* ignore malformed frame */
      }
    });
    es.addEventListener('chat.message', (ev) => {
      try {
        handlers.onMessage?.(JSON.parse((ev as MessageEvent).data) as LiveChatMessage);
      } catch {
        /* ignore malformed frame */
      }
    });
    es.addEventListener('error', () => {
      handlers.onStatus?.(false);
      es?.close();
      es = null;
      scheduleReconnect();
    });
  };

  void connect();

  return () => {
    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    es?.close();
    es = null;
  };
}
